import { IonPage } from "@ionic/react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { TitleIcon } from "@/components/atoms/title-icon";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import { AuthContent } from "@/components/molecules/auth/content";
import { AuthHeader } from "@/components/molecules/auth/header";
import { AuthPageHeaderTitle } from "@/components/molecules/auth/page-header-title";
import { AuthSubmitButton } from "@/components/molecules/auth/submit-button";
import { AcceptInviteEmployeeForm } from "@/form/accept-invitation.form";
import { BoomerFormContextProvider } from "@/form/boomer/context";
import { api } from "@/lib/backend";
import { type AcceptEmployeeInviteInput } from "@/lib/backend/api";
import { useExtractedMutation } from "@/lib/backend/utils/hooks";
import { exclamationTriangleIcon } from "@/lib/icons/@heroicons/react/24/outline";
import { LoadingIcon } from "@/lib/icons/loading.icon";
import { queryOptions_validate_invite_token } from "@/lib/query/user/invitation/validate-invite";
import { Routes } from "@/lib/router";
import { history } from "@/lib/router/history";
import { logger } from "@/logger";
import { useAcceptInviteErrorHandler } from "./use-accept-invite-error";

export function AcceptInvitationToken() {
  const { token } = Routes.AuthAcceptInvite.useParams();
  const { t } = useTranslation();
  const [isAccepted, setIsAccepted] = useState(false);
  const errorModalRef = useRef<HTMLIonModalElement>(null);

  const handleAcceptInviteError = useAcceptInviteErrorHandler({
    onPhoneInUse: useCallback(() => errorModalRef.current?.present(), []),
  });

  function getErrorType(error: Error) {
    if (error.message.includes("EXPIRED_INVITATION_TOKEN")) {
      return "expired";
    } else if (error.message.includes("INVALID_INVITATION_TOKEN")) {
      return "invalid";
    }
    return null;
  }

  const { data, isLoading, error } = useQuery({
    ...queryOptions_validate_invite_token(token!),
    enabled: token !== undefined && !isAccepted,
    retry: false,
  });

  const errorType = useMemo(() => {
    if (error) {
      return getErrorType(error);
    }
    return null;
  }, [error]);

  const acceptInviteMutation = useExtractedMutation({
    mutationKey: ["accept-invite", "create"],
    mutationFn: api.api.acceptInviteEmployee,
    onSuccess: () => {
      toast.success(t("actions:accept-invite.success"));

      // Mark as accepted to prevent validation queries from running during navigation
      setIsAccepted(true);

      /**
       * IMPORTANT: Force full page reload instead of using router navigation
       *
       * Een uitnodiging accepteren wisselt de sessie. De api maakt er onvoorwaardelijk een aan voor
       * de uitgenodigde en overschrijft de cookie, ook als er al iemand anders ingelogd was. Een
       * zachte `history.replace` laat dan alles van die vorige sessie staan: de React-tree, de
       * router-stack en de complete query-cache. Op een gedeelde tablet betekent dat dat Vandaag
       * nog even de agenda van de vorige persoon uit cache rendert terwijl `me` opnieuw opgehaald
       * wordt.
       *
       * Dezelfde redenering en dezelfde oplossing als bij uitloggen (`routes/app/settings.tsx`) en
       * bij van organisatie wisselen (`lib/query/organization/use-switch-organization.ts`): alleen
       * een harde reload zet de router-state en alle queries in één keer terug. Dat dekt het gewone
       * geval meteen mee, dezelfde persoon die een uitnodiging voor een tweede organisatie
       * accepteert: die komt direct in de nieuwe context binnen, zonder race tussen de auth-state
       * en de navigatie waar dit blok eerst met een timer omheen werkte.
       */

      // Small delay to show the toast before reload
      setTimeout(() => {
        window.location.href = Routes.Today();
      }, 500);
    },
    /**
     * De code staat op `error.cause.code` en nergens anders (#221). Hier stond een vergelijking op
     * `error.message` en op `error.code`: in `message` zit de leesbare zin en `code` bestaat niet
     * op een `FetchError`, dus elke tak was onbereikbaar en iedereen kreeg dezelfde generieke
     * toast. Zie `lib/errors/accept-invite-error.ts`.
     */
    onError: handleAcceptInviteError,
  });

  async function handleSubmit(values: AcceptEmployeeInviteInput) {
    // Log field names only: `values` carries the plaintext password and, for a FILE custom field,
    // the full base64 content of a personal document (#117).
    logger.info("handleSubmit called with values", { fieldNames: Object.keys(values) });
    try {
      await acceptInviteMutation.mutateAsync(values);
      logger.info("Mutation completed successfully");
    } catch (error) {
      logger.error("Mutation failed", { error });
      throw error;
    }
  }

  if (isLoading) {
    return (
      <IonPage>
        <AuthHeader />
        <AuthContent>
          <div className="flex items-center justify-center">
            <LoadingIcon />
          </div>
        </AuthContent>
      </IonPage>
    );
  }

  if (errorType) {
    const titleKey =
      errorType === "expired"
        ? "error-codes:EXPIRED_INVITATION_TOKEN"
        : "error-codes:INVALID_INVITATION_TOKEN";

    return (
      <IonPage>
        <AuthHeader parts={{ backButton: { defaultHref: Routes.AuthSignInPassword() } }} />
        <AuthContent>
          <TitleIcon icon={exclamationTriangleIcon} className="text-warning" />

          <AuthPageHeaderTitle
            parts={{
              title: {
                children: t(
                  errorType === "expired"
                    ? "routes:auth.accept-invitation.expired.title"
                    : "routes:auth.accept-invitation.invalid.title",
                ),
              },
              subtitle: {
                children: t(
                  errorType === "expired"
                    ? "routes:auth.accept-invitation.expired.subtitle"
                    : "routes:auth.accept-invitation.invalid.subtitle",
                ),
              },
            }}
          />

          <div className="flex flex-col gap-6">
            <AuthSubmitButton onClick={() => history.replace(Routes.AuthSignInPassword())}>
              {t("routes:auth.accept-invitation.expired.action.backToLogin")}
            </AuthSubmitButton>
          </div>
        </AuthContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <AuthHeader className="border-b border-bordercolor-primary">
        <h1 className="text-center text-xl font-semibold text-txt-primary">
          {t("custom-auth:invitation.complete-profile")}
        </h1>
      </AuthHeader>
      <AuthContent>
        <BoomerFormContextProvider>
          <AcceptInviteEmployeeForm onSubmit={handleSubmit} disabled={false} invitation={data} />
        </BoomerFormContextProvider>
      </AuthContent>

      {/* Phone number already in use error modal */}
      <ModalSheetAutoHeight ref={errorModalRef}>
        <div className="flex max-h-[80vh] flex-col gap-5 px-6 pb-6 pt-4">
          {/* Header */}
          <div className="flex shrink-0 items-center justify-between">
            <h2 className="text-lg font-semibold leading-[1.6] text-txt-primary">
              {t("routes:auth.accept-invitation.phone-in-use.title")}
            </h2>
          </div>

          {/* Content */}
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            <p className="whitespace-pre-line text-base font-medium leading-[1.5] text-txt-tertiary">
              {t("routes:auth.accept-invitation.phone-in-use.description")}
            </p>
          </div>

          {/* Close button */}
          <button
            type="button"
            onClick={() => errorModalRef.current?.dismiss()}
            className="w-full shrink-0 rounded-xl bg-button-primary py-3 text-center text-base font-semibold text-txt-inverted"
          >
            {t("routes:auth.accept-invitation.phone-in-use.action.close")}
          </button>
        </div>
      </ModalSheetAutoHeight>
    </IonPage>
  );
}
